import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { Link } from 'react-router-dom';
import {
  FiX,
  FiSearch,
  FiUser,
  FiBriefcase,
  FiExternalLink,
  FiCheckCircle,
  FiCalendar
} from 'react-icons/fi';
import '../styles/ManagerDashboard.css';
import '../styles/Submissions.css';

const STAGE_LABELS = {
  SUBMITTED_TO_CLIENT: 'Submitted to Client',
  CLIENT_REVIEW: 'Client Review',
  CLIENT_INTERVIEW_1: 'Client Round 1',
  CLIENT_INTERVIEW_2: 'Client Round 2',
  CLIENT_FINAL_ROUND: 'Client Final',
  OFFER: 'Offer',
  HIRED: 'Hired',
  PLACED: 'Placed',
  REJECTED: 'Rejected',
  REJECTED_INTERNAL: 'Rejected Internally',
  WITHDRAWN: 'Withdrawn'
};

const getStageBadgeStyle = (code) => {
  const c = (code || '').toUpperCase();
  if (c.includes('REJECT') || c.includes('WITHDRAW')) {
    return { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };
  }
  if (c.includes('HIRED') || c.includes('PLACED') || c.includes('OFFER')) {
    return { background: '#dcfce7', color: '#15803d', border: '1px solid #86efac' };
  }
  if (c.includes('INTERVIEW')) {
    return { background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' };
  }
  if (c.includes('REVIEW')) {
    return { background: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' };
  }
  return { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' };
};

const ClientSubmissionsModal = ({
  startDate,
  endDate,
  region,
  client,
  creationType,
  onClose
}) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch submitted candidates
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BASE_URL}/api/jobs/stats/client-submissions`,
          {
            params: {
              startDate,
              endDate,
              region: region || 'onsite',
              client: client ? (typeof client === 'object' ? client.name : client) : 'all',
              creationType: creationType || 'all'
            },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (response.data && response.data.success) {
          setCandidates(response.data.data || []);
        } else {
          setCandidates(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching client submitted candidates:', err);
        setError('Failed to load submitted candidate records.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [startDate, endDate, region, client, creationType]);

  // Client-side search filtering
  const filteredCandidates = useMemo(() => {
    if (!searchQuery.trim()) return candidates;
    const q = searchQuery.toLowerCase().trim();
    return candidates.filter((item) => {
      const name = (item.CandidateName || '').toLowerCase();
      const email = (item.CandidateEmail || '').toLowerCase();
      const phone = (item.CandidatePhone || '').toLowerCase();
      const jobCode = (item.JobCode || '').toLowerCase();
      const jobTitle = (item.JobTitle || '').toLowerCase();
      const recruiter = (item.RecruiterName || '').toLowerCase();
      const clientName = (item.ClientName || '').toLowerCase();
      const stage = (item.StageName || item.CurrentStageCode || '').toLowerCase();

      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        jobCode.includes(q) ||
        jobTitle.includes(q) ||
        recruiter.includes(q) ||
        clientName.includes(q) ||
        stage.includes(q)
      );
    });
  }, [candidates, searchQuery]);

  // Date formatting helpers
  const formatDateRange = () => {
    if (!startDate && !endDate) return 'All Dates';
    const s = startDate ? new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const e = endDate ? new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    return s && e ? `${s} — ${e}` : (s || e);
  };

  const formatSubmittedDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };


  const clientDisplayName = client ? (typeof client === 'object' ? client.name : client) : 'All Clients';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel"
        style={{
          maxWidth: '1280px',
          width: '96%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header */}
        <div
          className="modal-header"
          style={{
            background: 'linear-gradient(135deg, #f0f7ff 0%, #eff6ff 100%)',
            borderBottom: '1px solid #bfdbfe',
            padding: '16px 24px'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#dcfce7',
                  color: '#15803d'
                }}
              >
                <FiCheckCircle size={18} />
              </span>
              <div>
                <h3 className="modal-title" style={{ color: '#166534', margin: 0, fontSize: '18px', fontWeight: 700 }}>
                  Candidates Submitted to Client &mdash; {clientDisplayName}
                </h3>
                <p className="modal-subtitle" style={{ color: '#15803d', marginTop: '4px', fontSize: '12px', margin: 0 }}>
                  Period: <strong>{formatDateRange()}</strong> | Region: <strong style={{ textTransform: 'capitalize' }}>{region || 'Onsite'}</strong>
                  {creationType && creationType !== 'all' && (
                    <span> | Type: <strong style={{ textTransform: 'capitalize' }}>{creationType}</strong></span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: '#ffffff',
              border: '1px solid #d1fae5',
              color: '#374151',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Filter & Action Bar */}
        <div
          style={{
            padding: '12px 24px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ position: 'relative', flex: '1', minWidth: '260px', maxWidth: '450px' }}>
            <FiSearch
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8'
              }}
              size={16}
            />
            <input
              type="text"
              placeholder="Search by candidate, email, phone, role, recruiter..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
                background: '#ffffff'
              }}
            />
          </div>

          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
            Showing <strong>{filteredCandidates.length}</strong> of {candidates.length} candidate{candidates.length === 1 ? '' : 's'}
          </div>
        </div>

        {/* Modal Body Table with 9 Individual Columns */}
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div className="modal-loading" style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div className="modal-spinner" style={{ margin: '0 auto 12px auto' }} />
              <p style={{ color: '#64748b', fontSize: '14px' }}>Loading submitted candidates...</p>
            </div>
          ) : error ? (
            <div
              className="modal-error"
              style={{
                padding: '16px',
                background: '#fef2f2',
                color: '#b91c1c',
                borderRadius: '8px',
                border: '1px solid #fecaca',
                textAlign: 'center'
              }}
            >
              {error}
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="modal-empty" style={{ padding: '50px 20px', textAlign: 'center' }}>
              <FiBriefcase size={36} color="#94a3b8" style={{ marginBottom: '12px' }} />
              <p style={{ fontSize: '15px', color: '#475569', fontWeight: 600, margin: '0 0 6px 0' }}>
                {searchQuery ? 'No candidates match your search.' : `No submissions found for ${clientDisplayName} in this period.`}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0284c7',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Clear search filter
                </button>
              )}
            </div>
          ) : (
            <div className="modal-table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="submissions-table" style={{ fontSize: '13px', width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '10px 12px', minWidth: '150px', color: '#475569', fontWeight: 600 }}>Candidate Name</th>
                    <th style={{ padding: '10px 12px', minWidth: '180px', color: '#475569', fontWeight: 600 }}>Email</th>
                    <th style={{ padding: '10px 12px', minWidth: '130px', color: '#475569', fontWeight: 600 }}>Phone Number</th>
                    <th style={{ padding: '10px 12px', minWidth: '130px', color: '#475569', fontWeight: 600 }}>Client</th>
                    <th style={{ padding: '10px 12px', minWidth: '100px', color: '#475569', fontWeight: 600 }}>Job ID</th>
                    <th style={{ padding: '10px 12px', minWidth: '190px', color: '#475569', fontWeight: 600 }}>Job Title</th>
                    <th style={{ padding: '10px 12px', minWidth: '140px', color: '#475569', fontWeight: 600 }}>Assigned Recruiter</th>
                    <th style={{ padding: '10px 12px', minWidth: '130px', color: '#475569', fontWeight: 600, textAlign: 'center' }}>Stage</th>
                    <th style={{ padding: '10px 12px', minWidth: '110px', color: '#475569', fontWeight: 600, textAlign: 'right' }}>Submitted Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCandidates.map((row, idx) => {
                    const badgeStyle = getStageBadgeStyle(row.CurrentStageCode);
                    const stageDisplay = row.StageName || STAGE_LABELS[row.CurrentStageCode] || row.CurrentStageCode || 'Submitted to Client';

                    return (
                      <tr
                        key={row.SubmissionId || idx}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: idx % 2 === 0 ? '#ffffff' : '#fcfcfd',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#fcfcfd')}
                      >
                        {/* 1. Candidate Name */}
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b', fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {row.CandidateName || 'Unnamed Candidate'}
                        </td>

                        {/* 2. Email */}
                        <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                          {row.CandidateEmail ? (
                            <a
                              href={`mailto:${row.CandidateEmail}`}
                              style={{ color: '#0284c7', textDecoration: 'none' }}
                              title={`Email ${row.CandidateEmail}`}
                            >
                              {row.CandidateEmail}
                            </a>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>&mdash;</span>
                          )}
                        </td>

                        {/* 3. Phone Number */}
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: '#334151', whiteSpace: 'nowrap' }}>
                          {row.CandidatePhone ? (
                            <a
                              href={`tel:${row.CandidatePhone}`}
                              style={{ color: '#334151', textDecoration: 'none' }}
                              title={`Call ${row.CandidatePhone}`}
                            >
                              {row.CandidatePhone}
                            </a>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>&mdash;</span>
                          )}
                        </td>

                        {/* 4. Client */}
                        <td style={{ padding: '10px 12px', fontSize: '12.5px', fontWeight: 600, color: '#0f766e', whiteSpace: 'nowrap' }}>
                          {row.ClientName || 'Unknown'}
                        </td>

                        {/* 5. Job ID */}
                        <td style={{ padding: '10px 12px', fontSize: '11.5px', whiteSpace: 'nowrap' }}>
                          {row.JobCode ? (
                            <span
                              style={{
                                fontWeight: 700,
                                background: '#e0f2fe',
                                color: '#0369a1',
                                padding: '2px 7px',
                                borderRadius: '4px',
                                fontFamily: 'monospace'
                              }}
                            >
                              {row.JobCode}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>&mdash;</span>
                          )}
                        </td>

                        {/* 6. Job Title - Opens in new tab without closing popup */}
                        <td style={{ padding: '10px 12px' }}>
                          <Link
                            to={`/submissions/${row.JobId}`}
                            state={{ jobInfo: { id: row.JobId, jobId: row.JobCode, jobTitle: row.JobTitle, clientName: row.ClientName } }}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontWeight: 600,
                              color: '#0284c7',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Open job overview in new tab"
                          >
                            <span>{row.JobTitle || 'Untitled Position'}</span>
                            <FiExternalLink size={12} color="#0284c7" />
                          </Link>
                        </td>

                        {/* 7. Assigned Recruiter */}
                        <td style={{ padding: '10px 12px', fontSize: '12.5px', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#334151' }}>
                            <FiUser size={13} color="#64748b" />
                            <span>{row.RecruiterName || 'Unassigned'}</span>
                          </span>
                        </td>

                        {/* 8. Stage */}
                        <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              ...badgeStyle,
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontWeight: 600,
                              fontSize: '11.5px',
                              display: 'inline-block'
                            }}
                          >
                            {stageDisplay}
                          </span>
                        </td>

                        {/* 9. Submitted Date */}
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <FiCalendar size={12} color="#94a3b8" />
                            {formatSubmittedDate(row.SubmittedAt)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div
            className="modal-footer"
            style={{
              borderTop: '1px solid #e2e8f0',
              padding: '12px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f8fafc',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px'
            }}
          >
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              Showing <strong>{filteredCandidates.length}</strong> candidate{filteredCandidates.length === 1 ? '' : 's'} submitted to <strong>{clientDisplayName}</strong>
            </span>
            <button
              className="modal-close-footer-btn"
              onClick={onClose}
              style={{
                padding: '6px 18px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '13px',
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                color: '#334151'
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientSubmissionsModal;
